import Markdown from 'markdown-to-jsx'
import type { MarkdownToJSX } from 'markdown-to-jsx'
import type { ComponentPropsWithoutRef } from 'react'

import { termsStrings } from 'constants/strings'
import { formatDateTime } from 'utils'

type TermsDocumentProps = {
  serviceName: string
  title: string
  versionLabel: string
  bodyMarkdown: string
  publishedAt?: string | null
}

const ALLOWED_LINK_PROTOCOLS = new Set(['https:', 'http:', 'mailto:'])

/**
 * 規約本文中の URL を検証する。https / http / mailto とページ内リンクだけを通し、
 * javascript: や data: などは除去する（画像は https のみ）。
 */
export function sanitizeTermsUrl(
  value: string,
  tag: string,
  attribute: string,
): string | null {
  const trimmed = value.trim()
  if (attribute === 'href' && trimmed.startsWith('#')) return trimmed
  try {
    const url = new URL(trimmed)
    if (attribute === 'src' || tag === 'img') {
      return url.protocol === 'https:' ? url.href : null
    }
    return ALLOWED_LINK_PROTOCOLS.has(url.protocol) ? url.href : null
  } catch {
    // 相対URLは外部サイトの文脈で意味を持たないため表示しない
    return null
  }
}

function TermsLink({ href, children, ...props }: ComponentPropsWithoutRef<'a'>) {
  if (!href) return <span>{children}</span>
  const isInPage = href.startsWith('#')
  return (
    <a
      {...props}
      href={href}
      className="font-semibold text-accent underline underline-offset-2"
      {...(isInPage
        ? {}
        : { target: '_blank', rel: 'noopener noreferrer nofollow' })}
    >
      {children}
    </a>
  )
}

const markdownOptions: MarkdownToJSX.Options = {
  // 生HTMLはタグとして解釈せず、エスケープした文字のまま表示する
  disableParsingRawHTML: true,
  sanitizer: sanitizeTermsUrl,
  overrides: {
    a: { component: TermsLink },
    h1: { props: { className: 'mt-6 text-xl font-bold text-ink first:mt-0' } },
    h2: { props: { className: 'mt-6 text-lg font-bold text-ink first:mt-0' } },
    h3: { props: { className: 'mt-5 text-base font-bold text-ink first:mt-0' } },
    h4: { props: { className: 'mt-4 text-sm font-bold text-ink first:mt-0' } },
    p: { props: { className: 'mt-3 whitespace-pre-wrap leading-relaxed first:mt-0' } },
    ul: { props: { className: 'mt-3 list-disc space-y-1 pl-6' } },
    ol: { props: { className: 'mt-3 list-decimal space-y-1 pl-6' } },
    blockquote: {
      props: { className: 'mt-3 border-l-4 border-border/80 pl-4 text-muted' },
    },
    table: {
      props: { className: 'mt-3 w-full border-collapse text-left text-xs' },
    },
    th: { props: { className: 'border border-border/70 bg-surface px-2 py-1' } },
    td: { props: { className: 'border border-border/70 px-2 py-1 align-top' } },
    code: { props: { className: 'rounded bg-surface px-1 py-0.5 text-xs' } },
    hr: { props: { className: 'my-6 border-border/70' } },
    img: { props: { className: 'my-3 max-w-full' } },
  },
}

/** 顧客の同意画面と ADMIN のプレビューで共通の規約表示 */
export function TermsDocument({
  serviceName,
  title,
  versionLabel,
  bodyMarkdown,
  publishedAt,
}: TermsDocumentProps) {
  const strings = termsStrings.consent

  return (
    <article className="grid gap-4" aria-label={title}>
      <header className="grid gap-1">
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted">
          {serviceName}
        </span>
        <h2 className="text-xl font-bold text-ink">{title}</h2>
        <dl className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
          <div className="flex gap-1">
            <dt>{strings.versionLabel}:</dt>
            <dd className="font-semibold text-ink">{versionLabel}</dd>
          </div>
          {publishedAt && (
            <div className="flex gap-1">
              <dt>{strings.publishedAtLabel}:</dt>
              <dd className="font-semibold text-ink">
                {formatDateTime(publishedAt, { format: 'YYYY/MM/DD' })}
              </dd>
            </div>
          )}
        </dl>
      </header>
      <div
        className="max-h-[55vh] overflow-y-auto rounded-2xl border border-border/70 bg-white px-4 py-4 text-sm text-ink"
        data-testid="terms-body"
        tabIndex={0}
      >
        <Markdown options={markdownOptions}>{bodyMarkdown}</Markdown>
      </div>
    </article>
  )
}
